import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, IdCard, Plus } from "lucide-react";
import { toast } from "sonner";
import EntityDialog from "@/pages/people/EntityDialog";

export default function StudentsDirectory() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [families, setFamilies] = useState([]);
  const [levels, setLevels] = useState([]);
  const [cardMap, setCardMap] = useState({});
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("all");
  const [level, setLevel] = useState("all");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);

  const load = () => api.get("/students").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  useEffect(() => {
    load();
    api.get("/people/families").then((r) => setFamilies(r.data)).catch(() => {});
    api.get("/school/levels").then((r) => setLevels(r.data.items || r.data)).catch(() => {});
    api.get("/people/cards").then((r) => { const m = {}; (r.data || []).forEach((c) => { m[c.student_id] = c.status; }); setCardMap(m); }).catch(() => {});
  }, []);
  const famName = (id) => families.find((f) => f.id === id)?.name || "—";
  const lvlName = (id) => levels.find((l) => l.id === id)?.name || "—";
  const create = async (v) => { try { await api.post("/people/students", v); toast.success(t("save")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const filtered = rows.filter((s) =>
    (!q || (s.name || "").toLowerCase().includes(q.toLowerCase())) &&
    (family === "all" || s.family_id === family) &&
    (level === "all" || s.level_id === level) &&
    (status === "all" || (status === "card" && cardMap[s.id]) || (status === "nocard" && !cardMap[s.id]) ||
      (status === "archived" && s.status === "archived") || (status === "active" && s.status !== "archived")));

  return (
    <div data-testid="students-directory">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("students")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button className="gap-2" data-testid="add-student-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />{t("addStudent")}</Button>
          <Select value={family} onValueChange={setFamily}>
            <SelectTrigger className="w-40" data-testid="student-filter-family"><SelectValue placeholder={t("allFamilies")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allFamilies")}</SelectItem>{families.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-40" data-testid="student-filter-level"><SelectValue placeholder={t("allLevels")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("allLevels")}</SelectItem>{levels.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="student-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              <SelectItem value="active">active</SelectItem>
              <SelectItem value="archived">{t("archived")}</SelectItem>
              <SelectItem value="card">{t("cardStatus")}: ✓</SelectItem>
              <SelectItem value="nocard">{t("cardStatus")}: ✗</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="student-search" className="pl-9 w-48" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </div>
      <EntityDialog open={open} onOpenChange={setOpen} title={t("addStudent")} submitLabel={t("create")} onSubmit={create}
        fields={[{ name: "name", label: t("name") }, { name: "birthdate", label: t("birthdate"), inputType: "date" },
          { name: "family_id", label: t("family"), type: "select", options: families.map((f) => ({ value: f.id, label: f.name })) },
          { name: "level_id", label: t("level"), type: "select", options: levels.map((l) => ({ value: l.id, label: l.name })) }]} />
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("name")}</TableHead><TableHead>{t("level")}</TableHead><TableHead>{t("family")}</TableHead>
            <TableHead>{t("cardStatus")}</TableHead><TableHead>{t("status")}</TableHead><TableHead className="text-right">{t("viewFile")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((s) => (
              <TableRow key={s.id} data-testid="student-dir-row">
                <TableCell className="font-medium text-slate-800">{s.name}</TableCell>
                <TableCell className="text-slate-500">{lvlName(s.level_id)}</TableCell>
                <TableCell className="text-slate-500">{famName(s.family_id)}</TableCell>
                <TableCell>{cardMap[s.id] ? <Badge className="bg-emerald-100 text-emerald-700 border-0">{cardMap[s.id]}</Badge> : <span className="text-slate-300">—</span>}</TableCell>
                <TableCell className="text-slate-500">{s.status || "active"}</TableCell>
                <TableCell className="text-right">
                  <Link to={`/people/students/${s.id}`} data-testid={`open-profile-${s.id}`}>
                    <Button size="sm" variant="outline" className="gap-2"><IdCard className="h-4 w-4" />{t("viewFile")}</Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
